/**
 * #981 stage 2 — the browser's editing session of a large CSV (spec §8.2–§8.5;
 * docs/plans/2026-10-22_LARGE_CSV_FRAGMENTS_981.md): the per-file SESSION room (columns, the
 * server-written row counts, the file's save state) joined once, and the FRAGMENT rooms of the
 * rows in view — at most {@link MAX_JOINED_FRAGMENTS}, the least recently used left first.
 *
 * - DISPLAY INDEX. A displayed row's index walks the fragments by their LIVE row counts (a joined
 *   fragment's own doc; else the session's server-written count; else the manifest's). A fragment
 *   not joined is read through `/rows`, mapped back to its MANIFEST index (its base rows).
 * - ROW IDENTITY. An edit targets `(fragId, src)` — the source record in its fragment, stable under
 *   inserts — or, for a row read from a joined doc, its Yjs item (an inserted row has `src` -1 and
 *   is addressed only that way). A row deleted meanwhile refuses the edit (`row_deleted`).
 * - COLUMNS. Every edit maps a DISPLAY column through the session's `columns[i].slot`; a row is
 *   shown through `projectRow` with those columns, whether it came from a doc or from `/rows`
 *   (whose `fields` are slot-indexed in source order).
 * - STATE (MSG_CSV_STATE). `saving` disables editing; `reload` (and the room closes 4001 / 4404 /
 *   4409) re-opens the session — on an unseal the same base (the kept rows come back from the
 *   server), on a save the new one; the last save answer is handed to the next session (a failed
 *   save's unseal reloads right after its answer). `dirty` (the server's file-wide answer: someone's
 *   edit is persisted on this base), `conflicted` / `resolved`, `discard_failed {reason}` and
 *   `save_retry {reason}` reach the toolbar. Local edits mark `dirty` at once (a fast path).
 * - NEVER LOST SILENTLY (#981 final review). Save waits until every joined room acknowledged this
 *   client's edits (one round trip each, `CsvRoomConnection.roundTrip`) before MSG_SAVE — else it
 *   says "not synced", sends nothing; editing is off while a Save is pending. An edit a room refused
 *   (`edit_refused`), and unacknowledged edits a reload / a re-created room dropped, are said
 *   (`edits_lost`, carried across the reload). `save_refused {reason, encoding?}` is a save the
 *   server would not write (an unencodable value; edits under fragments the copy lacks).
 * - SLOTS. A cell edit names its column by SLOT (never a display index a peer's column change shifts).
 *   After the session room's first sync its doc stays the truth for columns and row counts, also
 *   while it reconnects; only a re-created (new epoch) session doc refuses edits until it syncs.
 */
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import * as Y from 'yjs'
import {
  columnsOf,
  fragmentRowCount,
  fragmentRowCountEntries,
  projectRow,
  setCell as modelSetCell,
  insertRows as modelInsertRows,
  deleteRows as modelDeleteRows,
  addColumn as modelAddColumn,
  renameColumn as modelRenameColumn,
  deleteColumn as modelDeleteColumn,
  type CsvColumn,
} from './large-csv-model.js'
import {
  connectCsvRoom,
  MSG_SAVE,
  MSG_FORCE_SAVE,
  MSG_DISCARD,
  type CsvRoomConnection,
  type CsvRoomConnector,
  type CsvStateMessage,
} from './csv-room.js'
import type { LargeCsvRow } from './large-csv-adapter.js'
import { foldFileState, type CollabFileState } from './useYjs.js'

export const MAX_JOINED_FRAGMENTS = 4
export const CSV_SESSION_FRAG = '_session'
const DEFAULT_SYNC_TIMEOUT_MS = 8_000
/**
 * #981 Task 11 — how long an EDIT waits for its fragment room's first sync. A read gives up at
 * {@link DEFAULT_SYNC_TIMEOUT_MS} (it shows the saved rows instead), but an edit has nothing to fall back on: on a busy
 * host a fragment room (a ~2 MB doc, created one at a time) took over 7 s to arrive (stagetest, QA D2), and the row is
 * already shown deleted meanwhile — so it waits longer rather than fail.
 */
const DEFAULT_EDIT_SYNC_TIMEOUT_MS = 30_000
/**
 * #981 Task 11 — a fragment room a VIEW read used this recently is not left for another view read. A sorted view's
 * blocks each want up to 4 different fragments; with two blocks on screen they took each other's rooms on every
 * re-read (joined and left within a millisecond, a ~2 MB room created on the server each time — QA D2 on stagetest:
 * 247 room joins in 4 minutes, 10–12 s per re-read, the API out of memory during that run). Now the block whose read came first keeps
 * them and the other shows its saved rows (plus this client's own changes) until those rooms go stale.
 */
const DEFAULT_VIEW_READ_HOLD_MS = 5_000
const DEFAULT_ACK_TIMEOUT_MS = 5_000
/** A Discard sent and not answered within this stops being pending (the server answers in well under a second). */
const DEFAULT_DISCARD_TIMEOUT_MS = 30_000
/**
 * A Save sent and neither started (`saving`: the server sealed the rooms, at the start of its flush) nor answered within
 * this was lost on the way (the socket dropped, the instance died before it took the seal). A started save is bounded
 * by the server: its seal lapses (SEAL_TTL_MS) and the rooms reload.
 */
const DEFAULT_SAVE_START_TIMEOUT_MS = 30_000
/**
 * #981 Task 11 review — the most fragment rooms ever joined: the cap plus this many for edits and reads that can't wait
 * for a slot. Past it an edit takes a room a view read is holding, else waits for one to be acknowledged (its row stays
 * shown deleted meanwhile); a read skips the fragment (its saved rows show).
 */
const JOIN_HEADROOM = 2
/** How often an edit waiting for a room slot looks again (a room becomes free once its edits are acknowledged). */
const SLOT_POLL_MS = 100

/** A row an edit addresses: its fragment, its source record (-1 = inserted), and its Yjs item if known. */
export interface CsvRowTarget {
  fragId: string
  src: number
  item?: { client: number; clock: number }
}

/** A displayed row: its display index, identity and output-order fields. */
export interface CsvSessionRow extends CsvRowTarget {
  row: number
  fields: string[]
  /** A view row whose record this client deleted (its fragment joined and synced, the record gone): shown struck through. */
  deleted?: true
}

/** `read_only`: this session's writes were refused here (#1315 A — see `writesRefusedHere`); the banner says why. */
export type CsvEditResult = 'ok' | 'row_deleted' | 'saving' | 'not_ready' | 'read_only'

export type CsvSessionNotice =
  | { kind: 'retry'; reason: string; nonce: number }
  | { kind: 'refused'; message: string; nonce: number }
  | { kind: 'failed'; nonce: number }
  | { kind: 'discard_failed'; reason: string; nonce: number }
  /** Edits of this client the server did not keep (refused while sealed, or dropped unacknowledged). */
  | { kind: 'edits_lost'; nonce: number }
  /** Save not sent: this client's last edits were not acknowledged in time. */
  | { kind: 'not_synced'; nonce: number }
  /** The server would not write the save (`unencodable` + `encoding`, `layout_changed`): nothing written, edits kept. */
  | { kind: 'save_refused'; reason: string; encoding?: string; nonce: number }

/** Why the session asks to re-open (from the server's reload `reason`; anything unknown is `other`). */
export type CsvReloadWhy =
  /** `sameRows` (#981 Task 9): the save inserted / deleted no row — the old view's order fits the new rows. */
  | { kind: 'saved'; base: string; sameRows?: true }
  | { kind: 'reset' }
  | { kind: 'unsealed' }
  | { kind: 'other' }

export interface LargeCsvSessionState {
  /** The session room has synced: columns and row counts are live. */
  ready: boolean
  columns: CsvColumn[]
  totalRows: number
  dirty: boolean
  conflicted: boolean
  /** A save streams the file: the rooms are read-only. */
  saving: boolean
  /** Save pressed, no answer yet. */
  savePending: boolean
  /** #981 Task 11 review: the pending Save is waiting for edits already under way (a delete still joining its room). */
  saveWaitsForEdits?: boolean
  /** Discard of the current base pressed, not answered yet (it may wait for edits under way): edits are refused, a repeat is ignored. */
  discardPending?: boolean
  notice: CsvSessionNotice | null
  /** The rooms closed for good (`forbidden` / `deleted` / `disconnected`): no editing. */
  /** `too_large`: a fragment's doc is too large for this server to host (#981 Task 12) — the file stays read-only. */
  ended: 'forbidden' | 'deleted' | 'disconnected' | 'too_large' | null
  /** Fragment ids joined now, least recently used first. */
  joined: string[]
  /** Bumps whenever displayed rows may have changed (a remote edit, a structural change). */
  version: number
  /**
   * #1315 A — the BANNER: the file's state as the editors show it, folded from the server's MSG_FILE_STATE frames by
   * `foldFileState` (the tab's per-room memory, shared with the live editors). A reload's new session starts with none.
   */
  fileState: CollabFileState
  /**
   * #1315 A — this session's writes were refused here: an `edit_refused`, or a `read-only` / `access-lowered` /
   * `being-deleted` frame — whatever the banner shows (a being-deleted room, a stale-permission join's `read-only`).
   * Gates editing (`read_only`). Never cleared in place: the server raises by a rejoin, and a RE-connection's epoch
   * while it is set reloads the session (its in-flight SYNC_STEP1 dropped), so edits refused meanwhile are never
   * replayed into the room.
   */
  writesRefusedHere: boolean
}

export interface LargeCsvSessionInit {
  /** `${tenantId}:${fileId}`. */
  room: string
  base: string
  /** The manifest's fragments, in file order. */
  fragments: { id: string; rows: number }[]
  /** The manifest header (the columns until the session room syncs). */
  header: string[]
  columnCount: number
  /** `/open` said this base already holds unsaved edits. */
  dirty?: boolean
  /** The session must be re-opened (a reload; a stale / not-ready base); `carry` = the save answer to keep showing. */
  onReload: (carry: CsvSessionNotice | null, why: CsvReloadWhy) => void
  /** A save answer the previous session (before a reload) was showing. */
  notice?: CsvSessionNotice | null
  maxJoined?: number
  syncTimeoutMs?: number
  /** How long an edit waits for its fragment room's first sync (default 30 s). */
  editSyncTimeoutMs?: number
  /** A fragment room a view read used within this many ms is not left for another view read (default 5 s). */
  viewReadHoldMs?: number
  /** How long a Discard sent to the server waits for its answer before it stops being pending (default 30 s). */
  discardTimeoutMs?: number
  /** How long a Save sent to the server waits for its first sign (`saving`, or its reply) before it stops being pending (default 30 s). */
  saveStartTimeoutMs?: number
  /** How long a Save waits for this client's edits to be acknowledged (default 5 s). */
  ackTimeoutMs?: number
  connect?: CsvRoomConnector
  shareToken?: string | null
  /** Start ended (no rooms joined): the grid gave up re-opening a session that kept closing. */
  ended?: 'disconnected'
}

export interface LargeCsvSession {
  getState(): LargeCsvSessionState
  subscribe(listener: () => void): () => void
  /** Joins the fragment rooms covering display rows `[start, end)` (≤ max), leaving the LRU ones. */
  joinForRows(start: number, end: number): Promise<void>
  /** Joins the fragments of a view's rows (the first `max` distinct ones), so their unsaved edits show. */
  joinForViewRows(rows: LargeCsvRow[]): Promise<void>
  /**
   * Joins these fragments (the first `max` distinct known ones) as a read does: their first sync is never a re-read.
   * A save's swap joins the new base's rooms of the rows on screen with it while the new sort / filter view is built
   * (#981: on a remote host those joins — each fragment doc is ~2 MB — were most of the wait after the view).
   */
  joinFragments(ids: string[]): Promise<void>
  /** Display rows `[start, start + count)`: from joined docs, else `fetchBase(manifestStart, n)`. */
  readRows(start: number, count: number, fetchBase: (manifestStart: number, count: number) => Promise<LargeCsvRow[]>): Promise<CsvSessionRow[]>
  /** A view's rows, shown through their fragment's doc when it is joined. */
  projectViewRows(rows: LargeCsvRow[]): CsvSessionRow[]
  /** The cell of `target` in column `slot` (the column's identity, never its display index). */
  setCell(target: CsvRowTarget, slot: string, value: string): Promise<CsvEditResult>
  insertRows(target: CsvRowTarget, n: number, where: 'above' | 'below'): Promise<CsvEditResult>
  deleteRows(targets: CsvRowTarget[]): Promise<CsvEditResult>
  addColumn(name: string): CsvEditResult
  renameColumn(displayCol: number, name: string): CsvEditResult
  deleteColumn(displayCol: number): CsvEditResult
  save(): void
  forceSave(): void
  /** Drops the unsaved edits — of this base, or of another (stale) `base` of the file. */
  discard(base?: string): void
  close(): void
}

const rowsArray = (doc: Y.Doc) => doc.getArray<Y.Map<unknown>>('rows')

/**
 * A row's Yjs item id — the one identity an INSERTED row (src -1) has, stable across re-joins (the seed is
 * deterministic and the stored state is a merge). `_item` is Yjs-internal (yjs 13: a type's `_item` is the Item
 * holding it, `id` its { client, clock }); there is no public accessor for a nested type's id. A Yjs upgrade must
 * re-check it (the 'inserted row' tests in LargeCsvGrid-edit.test.tsx exercise it).
 */
function itemIdOf(row: Y.Map<unknown>): { client: number; clock: number } | undefined {
  const id = (row as unknown as { _item?: { id: { client: number; clock: number } } | null })._item?.id
  return id ? { client: id.client, clock: id.clock } : undefined
}

/** The row's index in its fragment doc now, or -1 (deleted). */
function findRow(doc: Y.Doc, t: CsvRowTarget): number {
  const rows = rowsArray(doc).toArray()
  if (t.item) {
    const { client, clock } = t.item
    const i = rows.findIndex((r) => { const id = itemIdOf(r); return !!id && id.client === client && id.clock === clock })
    if (i >= 0 || t.src < 0) return i
  }
  if (t.src < 0) return -1
  return rows.findIndex((r) => r.get('src') === t.src)
}

function docRowToFields(row: Y.Map<unknown>): Record<string, string> {
  return (row.get('fields') as Y.Map<string>).toJSON() as Record<string, string>
}

function sourceFields(fields: string[]): Record<string, string> {
  const out: Record<string, string> = {}
  fields.forEach((v, i) => { out[String(i)] = v })
  return out
}

/** A connection that never connects: a session that starts ended. */
const deadConnector: CsvRoomConnector = (o) => {
  const doc = new Y.Doc()
  return {
    frag: o.frag, doc, synced: false,
    whenSynced: () => Promise.resolve(false),
    send: () => false,
    hasUnacked: () => false,
    roundTrip: () => Promise.resolve(false),
    close: () => { doc.destroy() },
  }
}

export function createLargeCsvSession(init: LargeCsvSessionInit): LargeCsvSession {
  const connect = init.ended ? deadConnector : (init.connect ?? connectCsvRoom)
  const maxJoined = init.maxJoined ?? MAX_JOINED_FRAGMENTS
  const joinCeiling = maxJoined + JOIN_HEADROOM
  const syncTimeoutMs = init.syncTimeoutMs ?? DEFAULT_SYNC_TIMEOUT_MS
  const editSyncTimeoutMs = init.editSyncTimeoutMs ?? DEFAULT_EDIT_SYNC_TIMEOUT_MS
  const viewReadHoldMs = init.viewReadHoldMs ?? DEFAULT_VIEW_READ_HOLD_MS
  const ackTimeoutMs = init.ackTimeoutMs ?? DEFAULT_ACK_TIMEOUT_MS
  const frags = init.fragments
  const fragIndex = new Map(frags.map((f, i) => [f.id, i]))
  const manifestStart: number[] = []
  {
    let acc = 0
    for (const f of frags) { manifestStart.push(acc); acc += f.rows }
  }
  const initialColumns: CsvColumn[] = Array.from({ length: init.columnCount }, (_, i) => ({ slot: String(i), name: init.header[i] ?? '' }))

  /** Joined fragment rooms; Map order = least recently used first. */
  const joined = new Map<string, CsvRoomConnection>()
  const listeners = new Set<() => void>()
  let closed = false
  let reloadAsked = false
  let nonce = 0
  let state: LargeCsvSessionState = {
    ready: false,
    columns: initialColumns,
    totalRows: frags.reduce((a, f) => a + f.rows, 0),
    dirty: !!init.dirty,
    conflicted: false,
    saving: false,
    savePending: false,
    notice: init.notice ?? null,
    ended: init.ended ?? null,
    joined: [],
    version: 0,
    fileState: null,
    writesRefusedHere: false,
  }

  const emit = () => { for (const l of listeners) l() }
  const patch = (p: Partial<LargeCsvSessionState>) => { state = { ...state, ...p }; emit() }

  const editsLost = () => {
    if (closed) return
    patch({ notice: { kind: 'edits_lost', nonce: ++nonce } })
  }

  const reload = (why: CsvReloadWhy = { kind: 'other' }) => {
    if (reloadAsked || closed) return
    reloadAsked = true
    // Edits not acknowledged yet go with this session: said, never dropped silently.
    if ([session, ...joined.values()].some((c) => c.hasUnacked())) editsLost()
    // a save's answer (a retry, a refusal) arrives right before the unseal's reload: keep it showing
    const carry = state.notice && state.notice.kind !== 'discard_failed' ? state.notice : null
    shutdown()
    init.onReload(carry, why)
  }

  // #981 follow-up: a Discard sent and never answered (the instance died, the socket dropped after the send) stops
  // being pending. After a drop the reconnect tells: rooms re-created (a new epoch) = the discard landed, quietly;
  // the same room synced again = it did not, said. Else the timer says so. Pressing Discard again is harmless.
  let discardTimer: ReturnType<typeof setTimeout> | null = null
  let discardDropped = false
  const clearDiscardTimer = () => {
    if (discardTimer !== null) { clearTimeout(discardTimer); discardTimer = null }
    discardDropped = false
  }
  const discardUnanswered = () => {
    clearDiscardTimer()
    if (closed || !state.discardPending) return
    patch({ discardPending: false, notice: { kind: 'discard_failed', reason: 'no_answer', nonce: ++nonce } })
  }
  let saveStartTimer: ReturnType<typeof setTimeout> | null = null
  const clearSaveStartTimer = () => { if (saveStartTimer !== null) { clearTimeout(saveStartTimer); saveStartTimer = null } }
  /** The notice the bound posted: taken back if the save shows up after all (it was slow, not lost). */
  let saveTimedOutNonce: number | null = null
  const saveNeverStarted = () => {
    saveStartTimer = null
    if (closed || !state.savePending || state.saving) return
    saveTimedOutNonce = ++nonce
    patch({ savePending: false, notice: { kind: 'failed', nonce: saveTimedOutNonce } })
  }
  /** The save showed up after its bound: no "couldn't save" for it (a newer notice stays). */
  const saveShowedUp = (): Partial<LargeCsvSessionState> => {
    const late = saveTimedOutNonce !== null && state.notice?.kind === 'failed' && state.notice.nonce === saveTimedOutNonce
    saveTimedOutNonce = null
    return late ? { notice: null } : {}
  }
  const discardLanded = () => {
    clearDiscardTimer()
    if (!closed && state.discardPending) patch({ discardPending: false })
  }

  const onState = (m: CsvStateMessage) => {
    switch (m.state) {
      case 'saving': clearSaveStartTimer(); patch({ saving: true, ...saveShowedUp() }); break
      case 'dirty': patch({ dirty: true }); break
      case 'reload': {
        const why: CsvReloadWhy = m.reason === 'csv_saved' && typeof m.base === 'string' && m.base
          ? { kind: 'saved', base: m.base, ...((m as { sameRows?: unknown }).sameRows === '1' ? { sameRows: true as const } : {}) }
          : m.reason === 'csv_reset' ? { kind: 'reset' }
          : m.reason === 'csv_unsealed' ? { kind: 'unsealed' }
          : { kind: 'other' }
        reload(why)
        break
      }
      case 'conflicted': patch({ conflicted: true }); break
      case 'resolved': patch({ conflicted: false }); break
      // `unknown_base` only answers an OTHER base's discard (`discard(base)`): a pending one of this base stays pending
      case 'discard_failed': if (m.reason !== 'unknown_base') clearDiscardTimer(); patch({ ...(m.reason !== 'unknown_base' ? { discardPending: false } : {}), notice: { kind: 'discard_failed', reason: m.reason ?? 'error', nonce: ++nonce } }); break
      case 'save_retry': patch({ notice: { kind: 'retry', reason: m.reason ?? 'flush_timeout', nonce: ++nonce } }); break
      case 'save_refused': {
        const encoding = (m as { encoding?: unknown }).encoding
        patch({ notice: { kind: 'save_refused', reason: m.reason ?? 'error', ...(typeof encoding === 'string' ? { encoding } : {}), nonce: ++nonce } })
        break
      }
      case 'edit_refused':
        // #1315 A: the room took none of it — no more edits here until a fresh session
        if (!closed) patch({ writesRefusedHere: true })
        editsLost()
        break
      default: break
    }
  }

  /** #1315 A — a MSG_FILE_STATE frame on any of this session's rooms. */
  const onFileState = (s: string) => {
    if (closed) return
    const refused = s === 'read-only' || s === 'access-lowered' || s === 'being-deleted'
    patch({ fileState: foldFileState(init.room, state.fileState, s), ...(refused ? { writesRefusedHere: true } : {}) })
  }

  /**
   * #1315 A — on a RE-connection's epoch with writes refused here, the session reloads — before this client answers
   * the server's SYNC_STEP1, which would hand the room the edits it refused (a raise's 4002 rejoin, a released delete);
   * the new session starts with no banner, and its joins' frames say what holds now. A new fragment room's FIRST epoch
   * never reloads (view reads join rooms as you scroll — a locked session would loop) and leaves the banner as it is:
   * every banner but "deleted" comes with `writesRefusedHere`, so a re-connection under one always reloads.
   */
  const onEpoch = (reconnected: boolean) => {
    if (closed) return
    if (reconnected && state.writesRefusedHere) reload()
  }

  const onEnd = (code: number) => {
    if (code === 4001 || code === 4404 || code === 4409) return reload({ kind: 'other' })
    patch({ ended: code === 4403 ? 'forbidden' : code === 4410 ? 'deleted' : code === 4414 ? 'too_large' : 'disconnected' })
  }

  // ── live counts and display mapping ──
  /** The session doc holds the live columns / counts: synced, or synced once and not re-created since (a reconnect keeps it). */
  let sessionEverSynced = false
  let sessionDocFresh = false
  const sessionUsable = () => session.synced || (sessionEverSynced && !sessionDocFresh)
  const liveCount = (i: number): number => {
    const f = frags[i]
    const conn = joined.get(f.id)
    if (conn?.synced) return rowsArray(conn.doc).length
    return sessionUsable() ? fragmentRowCount(session.doc, f.rows, f.id) : f.rows
  }
  const manifestTotal = frags.reduce((a, f) => a + f.rows, 0)
  /**
   * #981 Task 13 — the fragments whose LIVE count differs from the manifest's, as (index, live − manifest), in file
   * order: the session's written counts, a joined synced doc's own length over them. Few (edited / joined fragments),
   * so a lookup walks them and binary-searches the manifest's prefix sums between — never every fragment (a narrow
   * file is ~12,500 of them).
   */
  const liveDeltas = (): [number, number][] => {
    const d = new Map<number, number>()
    if (sessionUsable()) {
      for (const [id, n] of fragmentRowCountEntries(session.doc)) {
        const i = fragIndex.get(id)
        if (i !== undefined && n !== frags[i].rows) d.set(i, n - frags[i].rows)
      }
    }
    for (const [id, conn] of joined) {
      if (!conn.synced) continue
      const i = fragIndex.get(id)
      if (i === undefined) continue
      const n = rowsArray(conn.doc).length
      if (n !== frags[i].rows) d.set(i, n - frags[i].rows)
      else d.delete(i)
    }
    return [...d].sort((a, b) => a[0] - b[0])
  }
  const recount = () => {
    let total = manifestTotal
    for (const [, delta] of liveDeltas()) total += delta
    return total
  }
  /** The fragment in [lo, hi) holding MANIFEST row `m` (its live count = its manifest's there), or null. */
  const findIn = (lo: number, hi: number, m: number): { i: number; offset: number } | null => {
    if (lo >= hi) return null
    let a = lo
    let b = hi - 1
    while (a < b) {
      const mid = (a + b + 1) >> 1
      if (manifestStart[mid] <= m) a = mid
      else b = mid - 1
    }
    const f = frags[a]
    return m >= manifestStart[a] && m < manifestStart[a] + f.rows ? { i: a, offset: m - manifestStart[a] } : null
  }
  /** Display index → (fragment index, offset), by live counts. */
  const locate = (display: number): { i: number; offset: number } | null => {
    if (display < 0) return null
    let shift = 0
    let from = 0
    for (const [k, delta] of liveDeltas()) {
      const startK = manifestStart[k] + shift
      if (display < startK) return findIn(from, k, display - shift)
      const n = frags[k].rows + delta
      if (display < startK + n) return { i: k, offset: display - startK }
      shift += delta
      from = k + 1
    }
    return findIn(from, frags.length, display - shift)
  }
  const currentColumns = () => (sessionUsable() ? columnsOf(session.doc) : sessionEverSynced ? state.columns : initialColumns)

  /** Displayed rows may have changed: new counts / columns, and a `version` bump (the grid re-reads its rows). */
  const refresh = (extra: Partial<LargeCsvSessionState> = {}) => {
    if (closed) return
    patch({ columns: currentColumns(), totalRows: recount(), joined: [...joined.keys()], version: state.version + 1, ...extra })
  }
  /**
   * Which rooms are joined changed — NOT a re-read: the grid's reads join rooms, so a bump here would loop
   * (a re-read joins, the join asks for a re-read, …).
   */
  const noteJoined = () => {
    if (closed) return
    patch({ totalRows: recount(), joined: [...joined.keys()] })
  }
  /**
   * #981 Task 10 — this client's own unsaved changes to SOURCE records, by fragment: the records it deleted and the
   * cells it set (src → slot → value). A view row shows through its fragment's live doc while that room is joined —
   * but a view read joins only the first fragments of its block (≤ the cap), so the room of a row edited further down
   * is left once acknowledged, and the change vanished from the screen at the next read while it is still saved (QA
   * D2). For a fragment that is not joined, these are applied over the saved row instead. A joined, synced fragment's
   * live doc is the truth: the memory is reconciled with it whenever it is projected into a view AND when its room is
   * left (`reconcileLocal`) — a remembered cell takes the doc's value (a collaborator's later change included), and a
   * record the doc still holds is no longer remembered as deleted (the delete was lost: refused, or the room re-created
   * without it). So the memory holds the last state this client saw of each record it changed. The memory is the
   * session's: a save, a Discard or a reload starts a new session with none. A COLLABORATOR's edits past the cap that
   * this client never saw joined still show the saved value (only this client's are known here); Refresh view / Save
   * shows them.
   */
  const deletedHere = new Map<string, Set<number>>()
  const editedHere = new Map<string, Map<number, Map<string, string>>>()
  /** The fragment's room is joined and synced: the memory takes its live doc's state (the doc wins). */
  const reconcileLocal = (fragId: string, doc: Y.Doc) => {
    const gone = deletedHere.get(fragId)
    if (gone) {
      // still in the live doc: the delete never landed (or was dropped) — it won't be saved, so it isn't shown
      for (const src of gone) if (findRow(doc, { fragId, src }) >= 0) gone.delete(src)
      if (!gone.size) deletedHere.delete(fragId)
    }
    const byRow = editedHere.get(fragId)
    if (!byRow) return
    for (const [src, cells] of byRow) {
      const idx = findRow(doc, { fragId, src })
      if (idx < 0) {
        // gone from the live doc (a collaborator deleted it): no cells to show
        byRow.delete(src)
        continue
      }
      const live = docRowToFields(rowsArray(doc).get(idx))
      for (const slot of cells.keys()) cells.set(slot, live[slot] ?? '')
    }
    if (!byRow.size) editedHere.delete(fragId)
  }
  /** Fragment rooms a read is waiting on: their first sync is that read's, not a reason to re-read. */
  const awaitedByRead = new Set<CsvRoomConnection>()
  /**
   * #981 Task 11 — fragment rooms an edit is waiting on (joined, not synced yet): never left by a read's eviction. A
   * concurrent view read used to close the room a delete had just joined, and the delete failed with "not ready" (QA
   * D2: 3 of 3 deletes, 0.75–3 s after the click).
   */
  const awaitedByEdit = new Map<CsvRoomConnection, number>()
  /** When a view read last used each fragment's room (see {@link DEFAULT_VIEW_READ_HOLD_MS}). */
  const lastViewRead = new Map<string, number>()
  /** A view read used this fragment's room within the hold: an edit or another view read goes over the cap rather than take it. */
  const viewHeld = (id: string, now = Date.now()) => viewReadHoldMs > 0 && now - (lastViewRead.get(id) ?? -Infinity) < viewReadHoldMs
  /**
   * #981 Task 11 — the source records a delete of this client is applying (its fragment room still joining): shown
   * deleted AT ONCE, before the room arrives (QA D2: the mark took 10–12 s on stagetest). Dropped when the delete ends:
   * applied, it is in the live doc / {@link deletedHere}; failed, the row is shown as it is again.
   */
  const pendingDeletes = new Map<string, Map<number, number>>()
  const isPendingDelete = (fragId: string, src: number) => src >= 0 && !!pendingDeletes.get(fragId)?.get(src)
  const notePending = (targets: CsvRowTarget[], d: 1 | -1) => {
    for (const t of targets) {
      if (t.src < 0) continue
      let m = pendingDeletes.get(t.fragId)
      if (!m) pendingDeletes.set(t.fragId, (m = new Map()))
      const n = (m.get(t.src) ?? 0) + d
      if (n > 0) m.set(t.src, n)
      else m.delete(t.src)
      if (!m.size) pendingDeletes.delete(t.fragId)
    }
  }
  /** Edits under way (joining their room): a Save waits for them, so it never starts without the last one. */
  const inflightEdits = new Set<Promise<unknown>>()
  const track = <T,>(p: Promise<T>): Promise<T> => {
    inflightEdits.add(p)
    const done = () => { inflightEdits.delete(p) }
    p.then(done, done)
    return p
  }

  /** Columns or row counts differ from the base: the file has unsaved edits (someone's). */
  const sessionShowsEdits = (): boolean => {
    const cols = columnsOf(session.doc)
    if (cols.length !== initialColumns.length || cols.some((c, i) => c.slot !== initialColumns[i].slot || c.name !== initialColumns[i].name)) return true
    return frags.some((f) => fragmentRowCount(session.doc, f.rows, f.id) !== f.rows)
  }

  const common = { room: init.room, base: init.base, shareToken: init.shareToken, onState, onEnd, onLocalEditsLost: () => editsLost(), onFileState, onEpoch }
  const session = connect({
    ...common,
    frag: CSV_SESSION_FRAG,
    // the Discard's answer comes on this socket: once it drops, the answer may never come — the reconnect decides
    onDropped: () => { if (discardTimer !== null) discardDropped = true },
    onSynced: () => {
      // reconnected to the same room after a drop with a Discard pending: it was not done
      if (discardDropped && discardTimer !== null) discardUnanswered()
      sessionEverSynced = true
      sessionDocFresh = false
      refresh({ ready: true, dirty: state.dirty || sessionShowsEdits() })
    },
    onRemoteUpdate: () => refresh({ dirty: state.dirty || sessionShowsEdits() }),
    onDocReplaced: () => {
      // the session room was re-created while a Discard was pending: the discard landed
      if (discardTimer !== null) discardLanded()
      sessionDocFresh = true
      refresh({ ready: false })
    },
    onSaveReply: (ok, reason) => {
      clearSaveStartTimer()
      if (ok) { patch({ savePending: false, ...saveShowedUp() }); return }
      saveTimedOutNonce = null
      // A retry / refusal / conflict was already said (MSG_CSV_STATE / MSG_CONFLICT, sent before the ack).
      const said = ((state.notice?.kind === 'retry' || state.notice?.kind === 'save_refused') && state.savePending) || state.conflicted
      patch({
        savePending: false,
        notice: reason ? { kind: 'refused', message: reason, nonce: ++nonce } : said ? state.notice : { kind: 'failed', nonce: ++nonce },
      })
    },
    onConflict: (c) => patch({ conflicted: c }),
  })

  function joinFragment(fragId: string): CsvRoomConnection {
    const have = joined.get(fragId)
    if (have) {
      // most recently used goes last
      joined.delete(fragId)
      joined.set(fragId, have)
      return have
    }
    const conn: CsvRoomConnection = connect({
      ...common,
      frag: fragId,
      // a sync no read waited for (one that timed out, a reconnect) may change what is shown
      onSynced: () => { if (awaitedByRead.has(conn)) noteJoined(); else refresh() },
      onRemoteUpdate: () => refresh({ dirty: true }),
      onDocReplaced: () => refresh(),
    })
    joined.set(fragId, conn)
    return conn
  }

  /**
   * Leave the least recently used rooms past the cap — never one still holding unacknowledged edits (it stays joined,
   * over the cap, until acked), nor one a read or an edit is waiting on (#981 Task 11); `spare` keeps others too.
   */
  function evictBeyond(keep: Set<string>, spare: (id: string) => boolean = () => false) {
    for (const id of [...joined.keys()]) {
      if (joined.size <= maxJoined) break
      if (keep.has(id) || spare(id)) continue
      const conn = joined.get(id)!
      if (conn.hasUnacked() || awaitedByRead.has(conn) || awaitedByEdit.has(conn)) continue
      // the last state this client sees of its changed records there (a view read after this has no doc for them)
      if (conn.synced) reconcileLocal(id, conn.doc)
      conn.close()
      joined.delete(id)
    }
  }

  /** Leave ONE least recently used room that nothing is using (not `keep` / `spare`, no unacknowledged edits, not awaited). */
  function evictOne(keep: Set<string>, spare: (id: string) => boolean = () => false): boolean {
    for (const [id, conn] of joined) {
      if (keep.has(id) || spare(id)) continue
      if (conn.hasUnacked() || awaitedByRead.has(conn) || awaitedByEdit.has(conn)) continue
      if (conn.synced) reconcileLocal(id, conn.doc)
      conn.close()
      joined.delete(id)
      return true
    }
    return false
  }

  const withTimeout = (p: Promise<boolean>, ms = syncTimeoutMs) => new Promise<boolean>((resolve) => {
    const t = setTimeout(() => resolve(false), ms)
    p.then((ok) => { clearTimeout(t); resolve(ok) }, () => { clearTimeout(t); resolve(false) })
  })

  async function ensureJoined(fragId: string): Promise<CsvRoomConnection | null> {
    if (closed || !fragIndex.has(fragId)) return null
    const deadline = Date.now() + editSyncTimeoutMs
    // never past the ceiling: leave a room nobody uses, then one a view read is holding, else wait for a slot (an edit's
    // room is free once acknowledged) within this edit's budget — its row stays shown deleted meanwhile
    const keep = new Set([fragId])
    while (!joined.has(fragId) && joined.size >= joinCeiling) {
      if (evictOne(keep, viewHeld) || evictOne(keep)) continue
      if (closed || Date.now() >= deadline) return null
      await new Promise((r) => setTimeout(r, SLOT_POLL_MS))
      if (closed) return null
    }
    const conn = joinFragment(fragId)
    // the rooms the rows on screen were just read through stay (the block's re-read would only join them again)
    evictBeyond(new Set([fragId]), viewHeld)
    noteJoined()
    if (conn.synced) return conn
    // an edit's own join (a row out of view, a view's row): no read waits on it, so its sync re-reads the grid. Held
    // against every eviction while it waits, and given longer than a read (an edit has no saved rows to fall back on).
    awaitedByEdit.set(conn, (awaitedByEdit.get(conn) ?? 0) + 1)
    let ok = false
    try {
      ok = await withTimeout(conn.whenSynced(), Math.max(0, deadline - Date.now()))
    } finally {
      const n = (awaitedByEdit.get(conn) ?? 1) - 1
      if (n > 0) awaitedByEdit.set(conn, n)
      else awaitedByEdit.delete(conn)
    }
    return ok && joined.get(fragId) === conn ? conn : null
  }

  /** Joins `want` (≤ max) for a read and waits for their first sync; their syncs are the read's (no re-read). */
  async function joinForRead(want: string[], spare?: (id: string) => boolean) {
    // a file too large to edit here joins nothing more: the saved rows show (each join would be refused again)
    if (!want.length || state.ended === 'too_large') return
    const keep = new Set(want)
    // never past the ceiling: a fragment with no room left is read from its saved rows
    const conns: CsvRoomConnection[] = []
    for (const id of want) {
      if (!joined.has(id) && joined.size >= joinCeiling && !evictOne(keep, spare)) continue
      conns.push(joinFragment(id))
    }
    evictBeyond(keep, spare)
    noteJoined()
    const waiting = conns.filter((c) => !c.synced)
    for (const c of waiting) awaitedByRead.add(c)
    try {
      await Promise.all(waiting.map((c) => withTimeout(c.whenSynced())))
    } finally {
      for (const c of waiting) awaitedByRead.delete(c)
    }
  }

  /**
   * The first `max` distinct fragments of `ids` this base has, joined for a VIEW read — without taking a room another
   * view read used in the last {@link DEFAULT_VIEW_READ_HOLD_MS} (#981 Task 11): rooms already joined are reused, and
   * new ones only fill the slots left by rooms nobody is using; the rest of the rows show their saved values (plus this
   * client's own changes).
   */
  async function joinKnown(ids: string[]) {
    if (closed) return
    const want: string[] = []
    for (const id of ids) {
      if (want.length >= maxJoined) break
      if (fragIndex.has(id) && !want.includes(id)) want.push(id)
    }
    const now = Date.now()
    const held = (id: string) => viewHeld(id, now)
    // rooms that stay whatever this read does: wanted ones already joined, ones in use (edits, reads, unacked), fresh ones
    const staying = [...joined].filter(([id, c]) => want.includes(id) || held(id) || c.hasUnacked() || awaitedByRead.has(c) || awaitedByEdit.has(c)).length
    let room = Math.max(0, maxJoined - staying)
    const take = want.filter((id) => joined.has(id) || (room-- > 0))
    for (const [id, at] of lastViewRead) if (now - at >= viewReadHoldMs) lastViewRead.delete(id)
    for (const id of take) lastViewRead.set(id, now)
    await joinForRead(take, held)
  }

  const editBlocked = (): CsvEditResult | null => {
    if (closed || state.ended) return 'not_ready'
    // A pending Save is collecting this client's edits: one typed now would miss it.
    if (state.saving || state.savePending || state.discardPending) return 'saving'
    // #1315 A: the room refuses this client's writes (its access dropped, a delete holds it): none is sent
    if (state.writesRefusedHere) return 'read_only'
    return null
  }

  const ackWithin = (c: CsvRoomConnection) => new Promise<boolean>((resolve) => {
    const t = setTimeout(() => resolve(false), ackTimeoutMs)
    c.roundTrip().then((ok) => { clearTimeout(t); resolve(ok) }, () => { clearTimeout(t); resolve(false) })
  })

  /**
   * Pending BEFORE anything is awaited (an answer can arrive before `send` returns; editing stops now).
   * MSG_SAVE goes only once every room holding this client's edits acknowledged them — a Save never
   * starts without the last edit; if one can't be acknowledged in time, nothing is sent and it is said.
   */
  async function sendSave(type: number) {
    if (state.savePending || state.saving) return
    patch({ savePending: true, notice: null })
    // edits begun before Save was pressed (their room still joining — a delete is already shown done) go into it
    // (#981 Task 11): each lands or fails on its own bound before the acknowledgements are collected
    if (inflightEdits.size) {
      patch({ saveWaitsForEdits: true })
      await Promise.allSettled([...inflightEdits])
      if (closed) return
      patch({ saveWaitsForEdits: false })
    }
    if (closed) return
    const pending = [session, ...joined.values()].filter((c) => c.hasUnacked())
    const acked = await Promise.all(pending.map(ackWithin))
    if (closed) return
    if (acked.some((ok) => !ok)) {
      patch({ savePending: false, notice: { kind: 'not_synced', nonce: ++nonce } })
      return
    }
    if (!session.send(type)) { patch({ savePending: false, notice: { kind: 'failed', nonce: ++nonce } }); return }
    clearSaveStartTimer()
    saveStartTimer = setTimeout(saveNeverStarted, init.saveStartTimeoutMs ?? DEFAULT_SAVE_START_TIMEOUT_MS)
  }

  async function applySetCell(target: CsvRowTarget, slot: string, value: string): Promise<CsvEditResult> {
    // by SLOT: a peer's column change while the editor was open never moves the edit to another column
    if (!sessionUsable() || !currentColumns().some((c) => c.slot === slot)) return 'not_ready'
    const conn = await ensureJoined(target.fragId)
    if (!conn) return 'not_ready'
    if (state.saving) return 'saving'
    const idx = findRow(conn.doc, target)
    if (idx < 0) return 'row_deleted'
    modelSetCell(conn.doc, idx, slot, value)
    // remembered for view reads made after this room is left (an inserted row, src < 0, is never in a view)
    const src = rowsArray(conn.doc).get(idx).get('src')
    if (typeof src === 'number' && src >= 0) {
      let byRow = editedHere.get(target.fragId)
      if (!byRow) editedHere.set(target.fragId, (byRow = new Map()))
      let cells = byRow.get(src)
      if (!cells) byRow.set(src, (cells = new Map()))
      cells.set(slot, value)
    }
    patch({ dirty: true })
    return 'ok'
  }

  async function applyInsertRows(target: CsvRowTarget, n: number, where: 'above' | 'below'): Promise<CsvEditResult> {
    const conn = await ensureJoined(target.fragId)
    if (!conn) return 'not_ready'
    if (state.saving) return 'saving'
    const idx = findRow(conn.doc, target)
    if (idx < 0) return 'row_deleted'
    modelInsertRows(conn.doc, where === 'below' ? idx + 1 : idx, n)
    refresh({ dirty: true })
    return 'ok'
  }

  async function applyDeleteRows(targets: CsvRowTarget[]): Promise<CsvEditResult> {
    let missing = false
    const byFrag = new Map<string, CsvRowTarget[]>()
    for (const t of targets) byFrag.set(t.fragId, [...(byFrag.get(t.fragId) ?? []), t])
    for (const [fragId, ts] of byFrag) {
      const conn = await ensureJoined(fragId)
      if (!conn) return 'not_ready'
      if (state.saving) return 'saving'
      const idxs = ts.map((t) => findRow(conn.doc, t))
      if (idxs.some((i) => i < 0)) missing = true
      // the source records these rows hold (an inserted row has none: it is never in a view)
      for (const i of idxs) {
        const src = i >= 0 ? rowsArray(conn.doc).get(i).get('src') : undefined
        if (typeof src !== 'number' || src < 0) continue
        let set = deletedHere.get(fragId)
        if (!set) deletedHere.set(fragId, (set = new Set()))
        set.add(src)
      }
      conn.doc.transact(() => {
        for (const i of [...new Set(idxs.filter((x) => x >= 0))].sort((a, b) => b - a)) modelDeleteRows(conn.doc, i, 1)
      })
    }
    refresh({ dirty: true })
    return missing ? 'row_deleted' : 'ok'
  }

  function shutdown() {
    closed = true
    clearDiscardTimer()
    clearSaveStartTimer()
    for (const c of joined.values()) c.close()
    joined.clear()
    session.close()
  }

  return {
    getState: () => state,
    subscribe(l) { listeners.add(l); return () => { listeners.delete(l) } },

    async joinForRows(start, end) {
      if (closed) return
      const want: string[] = []
      for (let d = Math.max(0, start); d < end && want.length < maxJoined;) {
        const loc = locate(d)
        if (!loc) break
        want.push(frags[loc.i].id)
        d += liveCount(loc.i) - loc.offset
      }
      await joinForRead(want)
    },

    async joinForViewRows(rows) {
      await joinKnown(rows.map((r) => r.fragId))
    },

    joinFragments: (ids) => joinKnown(ids),


    async readRows(start, count, fetchBase) {
      const cols = currentColumns()
      const out: CsvSessionRow[] = []
      // Segments: a joined fragment's slice, or a run of manifest rows to fetch (merged when contiguous).
      type Fetch = { at: number; manifestStart: number; n: number }
      const fetches: Fetch[] = []
      let d = start
      const end = start + count
      while (d < end) {
        const loc = locate(d)
        if (!loc) break
        const f = frags[loc.i]
        const n = Math.min(end - d, liveCount(loc.i) - loc.offset)
        const conn = joined.get(f.id)
        if (conn?.synced) {
          const rows = rowsArray(conn.doc).toArray().slice(loc.offset, loc.offset + n)
          rows.forEach((r, k) => {
            const src = r.get('src') as number
            out.push({ row: d + k, fragId: f.id, src, item: itemIdOf(r), fields: projectRow(docRowToFields(r), cols), ...(isPendingDelete(f.id, src) ? { deleted: true as const } : {}) })
          })
        } else {
          // Not joined: its base rows, by manifest index (a live count past the manifest's shows blank rows).
          const inBase = Math.max(0, Math.min(n, f.rows - loc.offset))
          const at = out.length
          for (let k = 0; k < n; k++) out.push({ row: d + k, fragId: f.id, src: k < inBase ? loc.offset + k : -1, fields: projectRow({}, cols) })
          if (inBase > 0) {
            const ms = manifestStart[loc.i] + loc.offset
            const last = fetches[fetches.length - 1]
            if (last && last.manifestStart + last.n === ms && last.at + last.n === at) last.n += inBase
            else fetches.push({ at, manifestStart: ms, n: inBase })
          }
        }
        d += n
      }
      await Promise.all(fetches.map(async (fe) => {
        const rows = await fetchBase(fe.manifestStart, fe.n)
        rows.forEach((r, k) => {
          const slot = out[fe.at + k]
          if (!slot) return
          slot.fragId = r.fragId
          slot.src = r.src
          slot.fields = projectRow(sourceFields(r.fields), cols)
          if (isPendingDelete(r.fragId, r.src)) slot.deleted = true
        })
      }))
      return out
    },

    projectViewRows(rows) {
      const cols = currentColumns()
      for (const fragId of new Set(rows.map((r) => r.fragId))) {
        const conn = joined.get(fragId)
        if (conn?.synced) reconcileLocal(fragId, conn.doc)
      }
      return rows.map((r) => {
        const conn = joined.get(r.fragId)
        if (conn?.synced) {
          const idx = findRow(conn.doc, { fragId: r.fragId, src: r.src })
          if (idx >= 0) {
            const yr = rowsArray(conn.doc).get(idx)
            return { row: r.row, fragId: r.fragId, src: r.src, item: itemIdOf(yr), fields: projectRow(docRowToFields(yr), cols), ...(isPendingDelete(r.fragId, r.src) ? { deleted: true as const } : {}) }
          }
        }
        // joined AND synced, and the record is gone from the live doc: deleted, still in the saved file's view; not
        // joined: deleted only if this session deleted it (its room may have been left since) or is deleting it now
        const gone = r.src >= 0 && (conn?.synced ? true : !!deletedHere.get(r.fragId)?.has(r.src) || isPendingDelete(r.fragId, r.src))
        const edits = !conn?.synced && r.src >= 0 ? editedHere.get(r.fragId)?.get(r.src) : undefined
        const fields = edits ? { ...sourceFields(r.fields), ...Object.fromEntries(edits) } : sourceFields(r.fields)
        return { row: r.row, fragId: r.fragId, src: r.src, ...(gone ? { deleted: true as const } : {}), fields: projectRow(fields, cols) }
      })
    },

    setCell(target, slot, value) {
      const blocked = editBlocked()
      if (blocked) return Promise.resolve(blocked)
      return track(applySetCell(target, slot, value))
    },

    insertRows(target, n, where) {
      const blocked = editBlocked()
      if (blocked) return Promise.resolve(blocked)
      return track(applyInsertRows(target, n, where))
    },

    deleteRows(targets) {
      const blocked = editBlocked()
      if (blocked) return Promise.resolve(blocked)
      // shown deleted at once (the grid and every read until the delete ends), never after the room arrives
      notePending(targets, 1)
      const p = track(applyDeleteRows(targets))
      const done = () => notePending(targets, -1)
      p.then(done, done)
      return p
    },

    addColumn(name) {
      const blocked = editBlocked()
      if (blocked) return blocked
      if (!session.synced) return 'not_ready'
      modelAddColumn(session.doc, name)
      refresh({ dirty: true })
      return 'ok'
    },

    renameColumn(displayCol, name) {
      const blocked = editBlocked()
      if (blocked) return blocked
      const col = session.synced ? columnsOf(session.doc)[displayCol] : undefined
      if (!col) return 'not_ready'
      modelRenameColumn(session.doc, col.slot, name)
      refresh({ dirty: true })
      return 'ok'
    },

    deleteColumn(displayCol) {
      const blocked = editBlocked()
      if (blocked) return blocked
      const col = session.synced ? columnsOf(session.doc)[displayCol] : undefined
      if (!col) return 'not_ready'
      modelDeleteColumn(session.doc, col.slot)
      refresh({ dirty: true })
      return 'ok'
    },

    save() { void sendSave(MSG_SAVE) },
    forceSave() { void sendSave(MSG_FORCE_SAVE) },
    discard(base) {
      const payload = base && base !== init.base ? JSON.stringify({ base }) : undefined
      // #981 Task 11 review: discarding THIS base waits for edits already under way (a delete still joining its room),
      // so none lands in the doc after the server discarded it
      if (payload !== undefined) { session.send(MSG_DISCARD, payload); return }
      // the current base: one Discard at a time — pending until the server answers (a reload, or `discard_failed`)
      if (state.discardPending) return
      patch({ discardPending: true })
      const send = () => {
        if (!session.send(MSG_DISCARD)) { patch({ discardPending: false, notice: { kind: 'failed', nonce: ++nonce } }); return }
        discardTimer = setTimeout(discardUnanswered, init.discardTimeoutMs ?? DEFAULT_DISCARD_TIMEOUT_MS)
      }
      if (!inflightEdits.size) { send(); return }
      void Promise.allSettled([...inflightEdits]).then(() => { if (!closed) send() })
    },
    close() {
      if (!closed) shutdown()
    },
  }
}

export interface UseLargeCsvSessionOptions extends Omit<LargeCsvSessionInit, 'base'> {
  /** False: no rooms at all (read-only surfaces). */
  enabled: boolean
  /** A change starts a fresh session on the same base. */
  generation?: number
}

export type LargeCsvSessionApi = LargeCsvSessionState & Omit<LargeCsvSession, 'getState' | 'subscribe' | 'close'> & {
  /** The live row count NOW (not this render's): for reads that await joins. */
  currentTotal(): number
}

const noopSubscribe = () => () => {}

/**
 * The React face of {@link createLargeCsvSession}: one session per (file, base) while `enabled`;
 * `null` when editing is off. A StrictMode double effect closes the first session and opens a fresh one.
 */
export function useLargeCsvSession(fileId: string, base: string | null, opts: UseLargeCsvSessionOptions): LargeCsvSessionApi | null {
  const [session, setSession] = useState<LargeCsvSession | null>(null)
  const { enabled, room, generation } = opts
  // The options are read when the session starts; a new (file, base, room) starts a new session.
  const optsRef = useRef(opts)
  optsRef.current = opts
  useEffect(() => {
    if (!enabled || !base || !room) { setSession(null); return }
    const s = createLargeCsvSession({ ...optsRef.current, base, onReload: (carry, why) => optsRef.current.onReload(carry, why) })
    setSession(s)
    return () => s.close()
  }, [enabled, base, room, fileId, generation, optsRef])
  const state = useSyncExternalStore(session ? session.subscribe : noopSubscribe, () => session?.getState() ?? null)
  return useMemo(() => {
    if (!session || !state) return null
    return {
      ...state,
      currentTotal: () => session.getState().totalRows,
      joinForRows: session.joinForRows,
      joinForViewRows: session.joinForViewRows,
      joinFragments: session.joinFragments,
      readRows: session.readRows,
      projectViewRows: session.projectViewRows,
      setCell: session.setCell,
      insertRows: session.insertRows,
      deleteRows: session.deleteRows,
      addColumn: session.addColumn,
      renameColumn: session.renameColumn,
      deleteColumn: session.deleteColumn,
      save: session.save,
      forceSave: session.forceSave,
      discard: session.discard,
    }
  }, [session, state])
}
